import React, { CSSProperties, useCallback } from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../types';

interface ClickableDivProps {
  onClick: (() => void) | undefined;
  disabled?: boolean;
  style?: CSSProperties;
  className?: string;
  id?: string;
  stopPropagation?: boolean;
  testID?: string;
  ariaLabel?: string;
}

const ClickableDivComponent: React.FC<React.PropsWithChildren<ClickableDivProps>> = ({
  id,
  children,
  disabled = false,
  style = {},
  className,
  stopPropagation = false,
  onClick,
  testID,
  ariaLabel,
}) => {
  const isClickable = !!onClick && !disabled;
  const handleOnClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (!onClick || disabled) {
        return;
      }
      onClick();
      if (stopPropagation) {
        event.stopPropagation();
      }
    },
    [disabled, onClick, stopPropagation]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!onClick || disabled) {
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        if (event.key === ' ') {
          event.preventDefault();
        }
        onClick();
        if (stopPropagation) {
          event.stopPropagation();
        }
      }
    },
    [disabled, onClick, stopPropagation]
  );

  return (
    <div
      onClick={handleOnClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={isClickable ? 0 : -1}
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel}
      style={style}
      className={clsx(className, testID, { 'cursor-pointer': isClickable, 'cursor-auto': !isClickable })}
      id={id}
    >
      {children}
    </div>
  );
};

export const ClickableDiv = TypedMemo(ClickableDivComponent);
